import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import api from '../config/api';
import { showNotification } from '../components/NotificationManager';
import PropdataImportFloatingBar from '../components/PropdataImportFloatingBar';
import { createIntegrationJobPoller, integrationJobLabel, isActiveIntegrationJob, isSuccessfulIntegrationJob, isTerminalIntegrationJob, jobReadyForWork, unwrapIntegrationJob } from '../utils/integrationJobs';

const STORAGE_KEY = 'ipm_propdata_import_job';

function readStoredJob() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const o = JSON.parse(raw);
        if (o && o.jobId && o.userId) return o;
    } catch {
        /* ignore */
    }
    return null;
}

function processingModeFromJob(job) {
    if (!job) return null;
    if (job.processingMode === 'background') return 'background';
    if (job.processingMode == null || job.processingMode === 'browser') return 'browser';
    return null;
}

function writeStoredJob(jobId, userId, processingMode) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ jobId, userId, processingMode }));
    } catch {
        /* ignore */
    }
}

function clearStoredJob() {
    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch {
        /* ignore */
    }
}

function currentUserId() {
    try {
        const u = JSON.parse(localStorage.getItem('user') || 'null');
        return u && u._id ? String(u._id) : null;
    } catch {
        return null;
    }
}

const PropdataImportContext = createContext(null);

export function PropdataImportProvider({ children }) {
    const [jobId, setJobId] = useState(null);
    const [jobStatus, setJobStatus] = useState(null);
    const [processingMode, setProcessingMode] = useState(null);
    const [phaseUi, setPhaseUi] = useState('idle');
    const [monitoring, setMonitoring] = useState(false);
    const [pollError, setPollError] = useState('');
    const [actionBusy, setActionBusy] = useState(false);
    const userId = currentUserId();

    const beginPropdataImportJob = useCallback((newJobId, savedJob = null) => {
        const uid = currentUserId();
        if (!newJobId || !uid) return;
        const mode = processingModeFromJob(savedJob);
        writeStoredJob(newJobId, uid, mode);
        setProcessingMode(mode);
        setJobStatus(savedJob);
        setPollError('');
        setPhaseUi('running');
        setJobId(newJobId);
        setMonitoring(true);
    }, []);

    const clearJob = useCallback(() => {
        setMonitoring(false);
        setJobId(null);
        setJobStatus(null);
        setProcessingMode(null);
        setPollError('');
        setPhaseUi('idle');
        clearStoredJob();
    }, []);

    // The identifier contains no credentials and is tied to the account that created it.
    useEffect(() => {
        setJobId(null);
        setJobStatus(null);
        setProcessingMode(null);
        setMonitoring(false);
        setPhaseUi('idle');
        setPollError('');
        const stored = readStoredJob();
        const uid = userId;
        if (stored && uid && stored.userId === uid && stored.jobId) {
            setJobId(stored.jobId);
            setProcessingMode(['browser', 'background'].includes(stored.processingMode) ? stored.processingMode : null);
            setPhaseUi('running');
            setMonitoring(true);
        }
    }, [userId]);

    useEffect(() => {
        if (!jobId || !monitoring) return undefined;
        const ownerId = currentUserId();
        const endpoint = `/api/agency/migration/import/propdata-xlsx/status/${encodeURIComponent(jobId)}`;
        return createIntegrationJobPoller({
            fetchJob: async (isStopped) => {
                const assertOwner = () => {
                    if (!ownerId || currentUserId() !== ownerId) {
                        const denied = new Error('Session changed'); denied.response = { status: 403 }; throw denied;
                    }
                };
                assertOwner();
                let res = await api.get(endpoint, { timeout: 20000 });
                assertOwner();
                const saved = unwrapIntegrationJob(res.data);
                if (!isStopped() && currentUserId() === ownerId) {
                    const mode = processingModeFromJob(saved);
                    setProcessingMode(mode);
                    writeStoredJob(jobId, ownerId, mode);
                }
                // The persisted server mode controls every observation, including restored/409 jobs.
                if (!isStopped() && currentUserId() === ownerId && processingModeFromJob(saved) === 'browser' && jobReadyForWork(saved)) {
                    await api.post(endpoint, { action: 'process' }, { timeout: 30000 });
                    assertOwner();
                    res = await api.get(endpoint, { timeout: 20000 });
                    assertOwner();
                }
                return res.data;
            },
            onJob: (data) => {
                setJobStatus(data);
                setProcessingMode(processingModeFromJob(data));
                setPollError('');
                if (!isTerminalIntegrationJob(data)) return;
                setMonitoring(false);
                const summary = data.summary;
                const detail = summary
                    ? ` Properties: ${summary.propertiesCreated || 0} new, ${summary.propertiesUpdated || 0} updated, ${summary.propertiesSkipped || 0} skipped. Leads: ${summary.leadsImported || 0} new, ${summary.leadsUpdated || 0} updated, ${summary.leadsSkipped || 0} skipped.`
                    : '';
                if (isSuccessfulIntegrationJob(data)) {
                    setPhaseUi('done_ok');
                    clearStoredJob();
                    showNotification(`PropData import complete.${detail}`, 'success', 10000);
                } else {
                    setPhaseUi('done_err');
                    const message = data.status === 'cancelled' ? 'PropData import cancelled. Saved rows are retained.' : data.error || `PropData import ${integrationJobLabel(data).toLowerCase()}.`;
                    showNotification(message + detail, data.status === 'cancelled' ? 'info' : 'error', 12000);
                }
            },
            onRetry: (delay) => setPollError(`Status temporarily unavailable. Checking again in ${Math.ceil(delay / 1000)} seconds.`),
            onStop: (message) => {
                setMonitoring(false);
                setPollError(message);
                setPhaseUi('paused');
            },
        });
    }, [jobId, monitoring]);

    const checkPropdataImportJob = useCallback(() => {
        if (!jobId) return;
        setPollError('');
        setPhaseUi('running');
        setMonitoring(true);
    }, [jobId]);

    const changeJob = useCallback(async (action) => {
        if (!jobId) return;
        const ownerId = currentUserId();
        setActionBusy(true);
        setMonitoring(false);
        try {
            const res = await api.post(`/api/agency/migration/import/propdata-xlsx/status/${encodeURIComponent(jobId)}`, { action }, { timeout: 30000 });
            const saved = unwrapIntegrationJob(res.data);
            if (currentUserId() !== ownerId) return;
            const mode = processingModeFromJob(saved);
            setProcessingMode(mode);
            writeStoredJob(jobId, ownerId, mode);
            setJobStatus(saved);
            setPollError('');
            setPhaseUi('running');
            setMonitoring(true);
        } catch (error) {
            setPollError(error.response?.data?.message || `Could not ${action} this import.`);
            setPhaseUi('paused');
        } finally {
            setActionBusy(false);
        }
    }, [jobId]);
    const resumePropdataImportJob = useCallback(() => changeJob('resume'), [changeJob]);
    const cancelPropdataImportJob = useCallback(() => changeJob('cancel'), [changeJob]);

    const value = useMemo(
        () => ({
            propdataJobId: jobId,
            propdataJobStatus: jobStatus,
            propdataProcessingMode: processingMode,
            propdataJobLabel: jobStatus?.status === 'queued' && processingMode === 'background' && !jobStatus.cancelRequested
                ? 'Waiting for background processing' : integrationJobLabel(jobStatus),
            propdataImportPhaseUi: phaseUi,
            beginPropdataImportJob,
            propdataJobActive: !!jobId && (!jobStatus || isActiveIntegrationJob(jobStatus)),
            propdataPollError: pollError,
            propdataMonitoring: monitoring,
            propdataActionBusy: actionBusy,
            checkPropdataImportJob,
            resumePropdataImportJob,
            cancelPropdataImportJob,
            dismissPropdataImportJob: clearJob,
        }),
        [jobId, jobStatus, processingMode, phaseUi, beginPropdataImportJob, pollError, monitoring, actionBusy, checkPropdataImportJob, resumePropdataImportJob, cancelPropdataImportJob, clearJob],
    );

    return (
        <PropdataImportContext.Provider value={value}>
            {children}
            <PropdataImportFloatingBar />
        </PropdataImportContext.Provider>
    );
}

export function usePropdataImport() {
    const ctx = useContext(PropdataImportContext);
    if (!ctx) {
        throw new Error('usePropdataImport must be used within PropdataImportProvider');
    }
    return ctx;
}
